import { openModal } from "./modal";
import { escapeHtml, escapeHtmlAttribute } from "./escapeHtml";
import { showConfirmModal } from "./confirmModal";
import {
  OnlineClient,
  OnlineError,
  clearStoredSession,
  loadStoredSession,
  normalizeCode,
  saveStoredSession,
  type ChessRemoteState,
  type PublicSession,
} from "../net/onlineClient";
import type { Color } from "../game/types";

/** Resultat d'un appariement reussi : tout ce dont `mainOnline` a besoin pour demarrer. */
export interface OnlineMatch {
  client: OnlineClient;
  session: PublicSession;
  selfId: string;
  myColor: Color;
  names: Record<Color, string>;
}

const NAME_STORAGE_KEY = "dkg-chess-online-name";

function loadLastName(): string {
  try {
    return localStorage.getItem(NAME_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function saveLastName(name: string): void {
  try {
    localStorage.setItem(NAME_STORAGE_KEY, name);
  } catch {
    /* stockage indisponible : sans consequence */
  }
}

function errorMessage(err: unknown): string {
  if (err instanceof OnlineError) return err.message;
  if (err instanceof Error) return err.message;
  return "Erreur inconnue.";
}

/**
 * Couleur du joueur local. En partie, `colorOf` (issu du serveur) fait foi ;
 * en salon, l'ordre d'arrivee decide (cf. `ChessAdapter.createInitialState` :
 * players[0] = Blancs, players[1] = Noirs).
 */
function resolveColor(session: PublicSession, selfId: string, state: ChessRemoteState | null): Color {
  const fromState = state?.colorOf?.[selfId];
  if (fromState === "w" || fromState === "b") return fromState;
  return session.players[0]?.id === selfId ? "w" : "b";
}

/** Noms des deux camps, dans l'ordre du serveur (le premier joueur est Blanc). */
function resolveNames(session: PublicSession, selfId: string, state: ChessRemoteState | null): Record<Color, string> {
  const names: Record<Color, string> = { w: "Blancs", b: "Noirs" };
  const colorOf = state?.colorOf ?? null;
  session.players.forEach((player, index) => {
    const color: Color = colorOf?.[player.id] ?? (index === 0 ? "w" : "b");
    names[color] = player.name;
  });
  // Session encore incomplete : au moins le nom du joueur local est connu.
  const self = session.players.find((p) => p.id === selfId);
  if (self) names[resolveColor(session, selfId, state)] = self.name;
  return names;
}

/**
 * Flux complet "Jouer en ligne" : choix creer/rejoindre, puis salon d'attente
 * jusqu'au demarrage de la partie par le serveur. Resout avec `null` si le
 * joueur annule (le controleur reaffiche alors le menu principal).
 */
export function showOnlineMenu(): Promise<OnlineMatch | null> {
  return new Promise((resolve) => {
    const content = document.createElement("div");
    const { close } = openModal(content, { dismissible: false });
    const client = new OnlineClient();

    let settled = false;
    const finish = (match: OnlineMatch | null): void => {
      if (settled) return;
      settled = true;
      close();
      if (!match) client.dispose();
      resolve(match);
    };

    renderChoiceScreen(content, client, finish);
  });
}

// ---------------------------------------------------------------------------
// Ecran 1 : creer / rejoindre
// ---------------------------------------------------------------------------

function renderChoiceScreen(
  content: HTMLElement,
  client: OnlineClient,
  finish: (match: OnlineMatch | null) => void,
): void {
  const lastName = loadLastName();
  content.innerHTML = `
    <h2 class="title-display">Jouer en ligne</h2>
    <p class="subtitle">Affrontez un adversaire a distance</p>
    <div class="field">
      <label for="online-name-input">Votre pseudo</label>
      <input type="text" id="online-name-input" class="player-name-input" maxlength="18"
             placeholder="Votre pseudo" value="${escapeHtmlAttribute(lastName)}" />
    </div>
    <div class="field">
      <label for="online-code-input">Code de la partie (pour rejoindre)</label>
      <input type="text" id="online-code-input" class="player-name-input" maxlength="6"
             autocapitalize="characters" autocomplete="off" placeholder="ABC234" />
    </div>
    <p class="online-error" id="online-error" hidden></p>
    <div class="menu-actions">
      <button class="btn btn-primary" id="online-create-btn" type="button">Creer une partie</button>
      <button class="btn" id="online-join-btn" type="button">Rejoindre avec un code</button>
      <button class="btn btn-ghost" id="online-back-btn" type="button">Retour au menu</button>
    </div>
  `;

  const nameInput = content.querySelector<HTMLInputElement>("#online-name-input")!;
  const codeInput = content.querySelector<HTMLInputElement>("#online-code-input")!;
  const errorEl = content.querySelector<HTMLParagraphElement>("#online-error")!;
  const createBtn = content.querySelector<HTMLButtonElement>("#online-create-btn")!;
  const joinBtn = content.querySelector<HTMLButtonElement>("#online-join-btn")!;
  const backBtn = content.querySelector<HTMLButtonElement>("#online-back-btn")!;

  const showError = (message: string): void => {
    errorEl.textContent = message;
    errorEl.hidden = false;
  };
  const setBusy = (busy: boolean): void => {
    createBtn.disabled = busy;
    joinBtn.disabled = busy;
  };

  codeInput.addEventListener("input", () => {
    codeInput.value = normalizeCode(codeInput.value);
  });

  const readName = (): string | null => {
    const name = nameInput.value.trim();
    if (!name) {
      showError("Choisissez d'abord un pseudo.");
      return null;
    }
    saveLastName(name);
    return name;
  };

  const enterSession = async (kind: "create" | "join"): Promise<void> => {
    const name = readName();
    if (!name) return;
    const code = normalizeCode(codeInput.value);
    if (kind === "join" && code.length !== 6) {
      showError("Le code d'une partie compte 6 caracteres.");
      return;
    }
    errorEl.hidden = true;
    setBusy(true);
    try {
      await client.connect();
      const result = kind === "create" ? await client.createSession(name) : await client.joinSession(code, name);
      if (result.token) saveStoredSession({ code: result.session.code, token: result.token, playerName: name });
      renderLobbyScreen(content, client, result.session, result.selfId, finish);
    } catch (err) {
      showError(errorMessage(err));
      setBusy(false);
    }
  };

  createBtn.addEventListener("click", () => void enterSession("create"));
  joinBtn.addEventListener("click", () => void enterSession("join"));
  backBtn.addEventListener("click", () => finish(null));
}

// ---------------------------------------------------------------------------
// Ecran 2 : salon d'attente
// ---------------------------------------------------------------------------

function renderLobbyScreen(
  content: HTMLElement,
  client: OnlineClient,
  initialSession: PublicSession,
  selfId: string,
  finish: (match: OnlineMatch | null) => void,
): void {
  content.innerHTML = `
    <h2 class="title-display">Salon en ligne</h2>
    <p class="subtitle">Partagez ce code avec votre adversaire</p>
    <button class="btn online-code" id="online-code-display" type="button" title="Copier le code"></button>
    <ul class="online-players" id="online-players"></ul>
    <p class="subtitle" id="online-waiting">En attente d'un adversaire...</p>
    <p class="online-error" id="online-error" hidden></p>
    <div class="menu-actions">
      <button class="btn btn-primary" id="online-ready-btn" type="button">Je suis pret</button>
      <button class="btn btn-ghost" id="online-leave-btn" type="button">Quitter le salon</button>
    </div>
  `;

  const codeDisplay = content.querySelector<HTMLButtonElement>("#online-code-display")!;
  const playersEl = content.querySelector<HTMLUListElement>("#online-players")!;
  const waitingEl = content.querySelector<HTMLParagraphElement>("#online-waiting")!;
  const errorEl = content.querySelector<HTMLParagraphElement>("#online-error")!;
  const readyBtn = content.querySelector<HTMLButtonElement>("#online-ready-btn")!;
  const leaveBtn = content.querySelector<HTMLButtonElement>("#online-leave-btn")!;

  const showError = (message: string): void => {
    errorEl.textContent = message;
    errorEl.hidden = false;
  };

  codeDisplay.textContent = initialSession.code;
  codeDisplay.addEventListener("click", () => {
    void navigator.clipboard?.writeText(initialSession.code).then(
      () => {
        codeDisplay.textContent = "Copie !";
        window.setTimeout(() => {
          codeDisplay.textContent = initialSession.code;
        }, 1200);
      },
      () => {
        /* presse-papier refuse (http, permission) : le code reste lisible a l'ecran */
      },
    );
  });

  let session = initialSession;

  const render = (): void => {
    playersEl.innerHTML = session.players
      .map((player) => {
        const you = player.id === selfId ? " (vous)" : "";
        const state = !player.connected ? "deconnecte" : player.ready ? "pret" : "pas pret";
        return `<li class="online-player" data-ready="${player.ready}">
            <span class="online-player-name">${escapeHtml(player.name)}${you}</span>
            <span class="online-player-state">${state}</span>
          </li>`;
      })
      .join("");
    waitingEl.hidden = session.players.length >= 2;
    const self = session.players.find((p) => p.id === selfId);
    readyBtn.textContent = self?.ready ? "Pas pret" : "Je suis pret";
  };
  render();

  const onSessionUpdate = (next: PublicSession): void => {
    session = next;
    render();
    if (next.status === "playing") {
      const state = client.cachedState;
      finish({
        client,
        session: next,
        selfId,
        myColor: resolveColor(next, selfId, state),
        names: resolveNames(next, selfId, state),
      });
    }
  };
  client.on("session:update", onSessionUpdate);

  readyBtn.addEventListener("click", () => {
    const self = session.players.find((p) => p.id === selfId);
    const next = !self?.ready;
    readyBtn.disabled = true;
    errorEl.hidden = true;
    void client
      .setReady(next)
      .catch((err: unknown) => showError(errorMessage(err)))
      .finally(() => {
        readyBtn.disabled = false;
      });
  });

  leaveBtn.addEventListener("click", () => {
    leaveBtn.disabled = true;
    void client
      .leave()
      .catch(() => {
        /* deja parti / session close : on quitte quand meme l'ecran */
      })
      .finally(() => {
        client.off("session:update", onSessionUpdate);
        clearStoredSession();
        finish(null);
      });
  });
}

// ---------------------------------------------------------------------------
// Reprise d'une session apres rechargement de la page
// ---------------------------------------------------------------------------

/**
 * Si un jeton de session en ligne est encore en stockage, tente
 * `session:reconnect` et propose de reprendre la partie. Nettoie le stockage
 * (et resout `null`) si la session a expire, s'est terminee, ou si le joueur
 * refuse. N'affiche rien tant que la reconnexion n'a pas reussi : une session
 * morte doit rester invisible pour le joueur.
 */
export async function tryResumeOnlineSession(): Promise<OnlineMatch | null> {
  const stored = loadStoredSession();
  if (!stored) return null;

  const client = new OnlineClient();
  try {
    await client.connect();
    const result = await client.reconnect(stored.code, stored.token);
    if (result.session.status === "finished") {
      clearStoredSession();
      client.dispose();
      return null;
    }

    // Le serveur diffuse `game:state` juste apres l'ack de reconnexion : on lui
    // laisse le temps d'arriver pour connaitre les couleurs avant de reprendre.
    const state = result.session.status === "playing" ? await waitForState(client) : null;

    const resume = await showConfirmModal(
      `Une partie en ligne est en cours (code ${escapeHtml(result.session.code)}). Voulez-vous la reprendre ?`,
      "Reprendre",
    );
    if (!resume) {
      await client.leave().catch(() => {
        /* partie deja commencee : impossible de la quitter proprement, on l'abandonne simplement */
      });
      clearStoredSession();
      client.dispose();
      return null;
    }

    const session = client.cachedSession ?? result.session;
    if (session.status !== "playing") {
      // Toujours au salon : on y retourne via le flux normal plutot que de lancer une partie.
      client.dispose();
      return null;
    }
    return {
      client,
      session,
      selfId: result.selfId,
      myColor: resolveColor(session, result.selfId, state),
      names: resolveNames(session, result.selfId, state),
    };
  } catch {
    clearStoredSession();
    client.dispose();
    return null;
  }
}

function waitForState(client: OnlineClient, timeoutMs = 3000): Promise<ChessRemoteState | null> {
  if (client.cachedState) return Promise.resolve(client.cachedState);
  return new Promise((resolve) => {
    const done = (state: ChessRemoteState | null) => {
      window.clearTimeout(timer);
      client.off("game:state", onState);
      resolve(state);
    };
    const onState = (state: ChessRemoteState) => done(state);
    const timer = window.setTimeout(() => done(client.cachedState), timeoutMs);
    client.on("game:state", onState);
  });
}
